import plugin from "tailwindcss/plugin";
import tailwindAnimatePlugin from "tailwindcss-animate";
import { Breakpoints } from "./src/foundations/breakpoints/breakpoints";
import {
  ColorPrimitives,
  GradientPrimitives,
} from "./src/foundations/color/color-primitives";
import { ElevationPrimitives } from "./src/foundations/elevation/elevation-primitives";
import { RadiiPrimitives } from "./src/foundations/radii/radii-primitives";
import { SpacingPrimitives } from "./src/foundations/spacing/spacing-primitives";
import { SkipInkClasses } from "./src/foundations/typography/skip-ink-classes";
import { TypographyClasses } from "./src/foundations/typography/typography-classes";
import { TypographyPrimitives } from "./src/foundations/typography/typography-primitives";

/** @type {import('tailwindcss').Config} */
export const baseConfig = {
  darkMode: ["class"],
  prefix: "tw-",
  theme: {
    screens: Object.keys(Breakpoints).reduce(
      (breakpoints, size) => {
        breakpoints[size.toLowerCase()] =
          `${Breakpoints[size as keyof typeof Breakpoints]}px`;
        return breakpoints;
      },
      {} as Record<string, string>,
    ),
    colors: ({ colors }: { colors: any }) => ({
      ...ColorPrimitives,
      transparent: colors.transparent,
      current: colors.current,
      inherit: colors.inherit,
    }),
    borderRadius: RadiiPrimitives,
    spacing: SpacingPrimitives,
    boxShadow: ElevationPrimitives,
    extend: {
      ...TypographyPrimitives,
      backgroundImage: GradientPrimitives,
      animation: {
        spin: "spin 0.75s linear infinite",
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
        "collapsible-down": "collapsible-down 0.2s ease-out",
        "collapsible-up": "collapsible-up 0.2s ease-out",
      },
      zIndex: {
        max: 9999,
      },
      keyframes: {
        "accordion-down": {
          from: {
            height: "0",
          },
          to: {
            height: "var(--radix-accordion-content-height)",
          },
        },
        "accordion-up": {
          from: {
            height: "var(--radix-accordion-content-height)",
          },
          to: {
            height: "0",
          },
        },
        "collapsible-down": {
          from: {
            height: "0",
          },
          to: {
            height: "var(--radix-collapsible-content-height)",
          },
        },
        "collapsible-up": {
          from: {
            height: "var(--radix-collapsible-content-height)",
          },
          to: {
            height: "0",
          },
        },
      },
    },
  },
  safelist: [
    // @TODO set up tailwind in web-2 and remove these
    { pattern: /tw-items-./ },
    { pattern: /tw-ml-./ },
    // @TODO only in dev mode
    ...Object.keys(ElevationPrimitives).map((key) => `tw-${key}`),
    ...Object.keys(RadiiPrimitives).map((key) => `tw-rounded-${key}`),
    ...Object.keys(TypographyClasses).flatMap((key) => {
      const className = `tw-${key.replace(".", "")}`;
      return [
        className,
        ...Object.keys(Breakpoints).map(
          (bp) => `${bp.toLowerCase()}:${className}`,
        ),
      ];
    }),
    ...Object.keys(SpacingPrimitives).map((key) => `tw-size-${key}`),
    ...Object.keys(SkipInkClasses).map((key) => `tw-${key}`),
    ...Object.keys(GradientPrimitives).map((key) => `tw-bg-${key}`),
    ...Object.keys(GradientPrimitives).map(
      (key) => `tw-gradient-border-${key}`,
    ),
  ],
  plugins: [
    tailwindAnimatePlugin,
    plugin(function ({ addUtilities }) {
      addUtilities(TypographyClasses);
      addUtilities(SkipInkClasses);

      // Gradient-border utilities. Each utility paints the border area with
      // its gradient and the padding-box with a solid colour from the
      // `--gradient-inner-bg` CSS variable. Callers set `--gradient-inner-bg`
      // (and switch it on hover) to control the inner fill.
      const gradientBorderUtilities = Object.entries(GradientPrimitives).reduce(
        (acc, [name, gradient]) => {
          acc[`.tw-gradient-border-${name}`] = {
            "background-image": `linear-gradient(var(--gradient-inner-bg, #ffffff), var(--gradient-inner-bg, #ffffff)), ${gradient}`,
            "background-origin": "border-box",
            "background-clip": "padding-box, border-box",
            "border-color": "transparent",
          };
          return acc;
        },
        {} as Record<string, Record<string, string>>,
      );
      addUtilities(gradientBorderUtilities);
    }),
  ],
};
